import React, { useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import type {
  FormResponses,
  FormResult,
  Invitation,
  SigninRequirements,
  SignoutRequirements,
} from '@sine/sine-core-api-types';
import type { ISelectOption, JoiForm } from '@sine/sine-ui-components';
import { Button, ButtonGroup, CheckBox, Loader, Modal, spacing, useNotification } from '@sine/sine-ui-components';

import type { PassWithResponseSummaries } from '../../../activity/helpers/activityFeedCache.helpers';
import parseServerError from '../../../helpers/parseServerError';
import formService from '../../../services/form/form.service';
import { useSitesWithPermissionsQuery } from '../../../services/site/site.hooks';
import { storage } from '../../../services/storage/storage.service';
import visitService from '../../../services/visit/visit.service';
import { useInvitationDetailsQuery } from '../../../shared/hooks/useInvitationDetailsQuery';
import { AclAction } from '../../../team/permission/constants/actions';
import { applyFormResponseToPass } from '../helpers/applyFormValues';
import SiteCheckInForm from './siteCheckInForm';
import type { PrePass, VisitorDetailsFormValues } from './visitorCheckInModal.types';
import VisitorDetailsForm from './visitorDetailsForm';

const PAGE = {
  VISITOR_DETAILS: 'visitorDetails',
  CHECK_IN_FORM: 'checkInForm',
};

const StyledModal = styled(Modal)`
  > div > div > div {
    word-wrap: break-word;

    h2 {
      padding-right: ${spacing(3)};
    }
  }
`;

const StyledCheckBox = styled(CheckBox)`
  padding: 0;
  margin: 0;

  span {
    min-height: 0;
  }
`;

const FooterContainer = styled.div<{ rightAlign: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props) => (props.rightAlign ? 'flex-end' : 'space-between')};
`;

function getCheckInAnotherSetting() {
  const storageItem = storage.getItem('checkinAnother');

  if (storageItem) {
    return JSON.parse(storageItem);
  }

  return false;
}

interface VisitorCheckInModalProps {
  invitation?: Invitation;
  pass?: PassWithResponseSummaries;
  show: boolean;
  onClose: () => void;
}

export default function VisitorCheckInModal({ invitation, pass, show, onClose }: VisitorCheckInModalProps) {
  const { addNotification } = useNotification();
  const { invitationInfo: invitationWithFormResponses, isFetchingInvitation } = useInvitationDetailsQuery(
    invitation?.id,
  );

  const [page, setPage] = useState(PAGE.VISITOR_DETAILS);
  const [isLoadingCount, setIsLoadingCount] = useState(0);
  const incrementLoadingCount = () => setIsLoadingCount((prev) => prev + 1);
  const decrementLoadingCount = () => setIsLoadingCount((prev) => prev - 1);
  const [siteRequirements, setSiteRequirements] = useState<SigninRequirements | SignoutRequirements>();
  const [selectedSite, setSelectedSite] = useState<ISelectOption>();
  const [selectedVisitorType, setSelectedVisitorType] = useState<ISelectOption>();
  const [checkInAnother, setCheckInAnother] = useState<boolean | undefined>(false);
  const [prefilledForm, setPrefilledForm] = useState<FormResponses | FormResult | null>(null);
  const [newPassForm, setNewPassForm] = useState<PrePass>();
  const [formRequired, setFormRequired] = useState<boolean>();
  const [editVisitorDetailsForm, setEditVisitorDetailsForm] = useState(false);

  const visitorDetailsFormRef = useRef<JoiForm<VisitorDetailsFormValues>>();
  const checkInFormRef = useRef<JoiForm<any>>();
  const firstNameInput = useRef<HTMLInputElement>(null);

  const hasCheckInForm = !!siteRequirements?.form;

  const prefillPass = useMemo(() => {
    return {
      invitation: invitation?.id,
      firstName: invitation?.firstName || pass?.firstName,
      lastName: invitation?.lastName || pass?.lastName,
      email: invitation?.email || pass?.email || '',
      mobile: invitation?.mobile || pass?.mobile || '',
      company: invitation?.companyDetails?.name || pass?.companyDetails?.name,
      // TODO: There's only one instance of invitation getting companyDetails from a pass (updateInvitationFromPass), in reality
      // client should not be getting companyDetails.id and companyDetails.companySource from invitation ever,
      // keeping the fallbacks until all visitor files are updated to TS
      companyId: (invitation?.companyDetails as any)?.id || pass?.companyDetails?.id,
      companySource: (invitation?.companyDetails as any)?.companySource || pass?.companyDetails?.companySource,
      adminNotes: invitation?.adminNotes,
      visitorNotes: invitation?.visitorNotes,
      // TODO: These siteId and hostId fallbacks shouldn't be necessary anymore but we keep them until all these visitor files are updated to TS
      site: invitation ? invitation.site || (invitation as any).siteId : pass ? pass.site : undefined,
      host: invitation ? invitation.host || (invitation as any).hostId : pass ? pass.host : undefined,
      visitorType: invitation ? invitation.visitorType : pass ? pass.visitorType : undefined,
      photoURL: invitation?.photoURL || pass?.photoURL,
      zone: invitation?.zoneId || pass?.zoneId,
    };
  }, [invitation, pass]);

  const { isFetchingSites, sites } = useSitesWithPermissionsQuery([AclAction.SIGN_IN, AclAction.H_PROFILE_PASS]);

  const { siteOptions } = useMemo(() => {
    let options = sites
      .filter((site) => site.settings.hostRequiredForSignIn || site.permissions.includes(AclAction.SIGN_IN))
      .map((site) => ({
        value: site.id,
        label: site.name,
        countryCode: site.countryCode,
      }));

    if (invitation) {
      // TODO: Same as above, siteId fallback shouldn't be necessary
      options = options.filter((site) => site.value === invitation.site || site.value === (invitation as any).siteId);
    }

    setCheckInAnother(getCheckInAnotherSetting());

    return { siteOptions: options };
  }, [sites, invitation]);

  const isLoading = useMemo(() => {
    return isFetchingInvitation || !!isLoadingCount;
  }, [isFetchingInvitation, isLoadingCount]);

  useEffect(() => {
    setFormRequired(
      formService.checkFormVisitorTypes(
        siteRequirements?.form?.fields,
        visitorDetailsFormRef.current?.values.visitorType?.value as string,
      ),
    );
  }, [visitorDetailsFormRef.current?.values.visitorType?.value]);

  // TODO: use react-query
  useEffect(() => {
    async function loadSiteRequirements() {
      if (selectedSite) {
        try {
          incrementLoadingCount();

          const _siteRequirements = await visitService.requirements(selectedSite.value as string);

          setSiteRequirements(_siteRequirements);
        } catch (error) {
          error.customMsg = 'Something went wrong';
          addNotification({
            message: parseServerError(error),
            variant: 'danger',
          });
        } finally {
          decrementLoadingCount();
        }
      }
    }

    void loadSiteRequirements();
  }, [selectedSite]);

  const identityCheck = async () => {
    const { firstName, lastName, email, mobile, company, companyId, companySource, zone } =
      visitorDetailsFormRef.current!.values;
    const { invitation: _invitation, adminNotes, visitorNotes } = prefillPass;

    const { token } = await visitService.identityCheck(selectedSite!.value as string, {
      firstName,
      lastName,
      email,
      mobile,
      company: company,
      companyId,
      companySource,
      visitorType: selectedVisitorType!.value as string,
      adminNotes: adminNotes!,
      visitorNotes: visitorNotes!,
      invitationId: _invitation,
      ...(zone?.value && { zoneId: zone.value as string }),
    });

    return token;
  };

  const createPass = async (newPass: PrePass) => {
    // create pass
    // if invitation used to prefill, create pass using invitation
    // if invitation not used to prefill, just create pass
    let shouldCloseModal = true;

    incrementLoadingCount();
    try {
      const createdPass = invitation
        ? await visitService.signInInvitation(invitation.id, {
            siteId: newPass.siteId,
            hostId: newPass.hostId,
            zoneId: newPass.zoneId,
            formResponses: newPass.formResponses,
            identityToken: newPass.identityToken,
          })
        : await visitService.signIn({
            ...newPass,
            // TODO: signinMethod is optional in the API since it detaults to 'default' value, but the TS type requires it
            // SignInVisitorRequest needs to be updated in the core api types package
            signinMethod: 'default',
          });

      if (createdPass.status === 'active') {
        addNotification({
          message: 'A pass has been created',
          variant: 'success',
        });
      }

      if (createdPass.status === 'pending') {
        addNotification({
          message: 'A pass is awaiting host approval',
          variant: 'success',
        });
      }

      return createdPass;
    } catch (error) {
      let errorMsg;
      let errorHeading;

      switch (error?.code) {
        case 'INTEGRATION_REQUEST_REJECTED':
          errorMsg = error?.message || 'This pass has been rejected';
          break;

        case 'INTEGRATION_REQUEST_FAILED':
          errorMsg =
            error?.message ||
            'An integration configured for this site has failed. Please try again later or contact support';
          break;

        case 'INTEGRATION_REQUEST_FAILED_UPSTREAM':
          errorMsg =
            'An integration configured for this site has failed. The upstream provider is not accessible. Please try again later or contact support.';
          break;

        case 'SITE_NOT_FOUND':
        case 'HOST_NOT_FOUND':
        case 'ZONE_NOT_FOUND':
        case 'ZONE_HOST_GROUP_MAPPING_NOT_FOUND':
          errorHeading = `Sorry, we couldn't sign this person in!`;
          errorMsg = parseServerError(error);
          shouldCloseModal = false;
          break;

        default:
          errorHeading = `Sorry, we couldn't sign this person in!`;
          errorMsg = parseServerError(error);
      }

      addNotification({
        heading: errorHeading,
        message: errorMsg,
        variant: 'danger',
      });
    } finally {
      decrementLoadingCount();
      setEditVisitorDetailsForm(false);

      if (checkInAnother && !pass && !invitation) {
        setPage(PAGE.VISITOR_DETAILS);
        visitorDetailsFormRef.current?.onChangeAll({
          firstName: '',
          lastName: '',
          company: '',
          email: '',
          mobile: '',
          site: siteOptions[0],
          visitorType: null,
          host: null,
          zone: null,
        });

        firstNameInput.current?.focus();
      } else {
        shouldCloseModal && onClose();
      }
    }
  };

  const applyFormResponses = async (newPass: PrePass) => {
    // validate check-in form and add the formResponses to the pass
    // go to createPass
    try {
      if (formRequired) {
        checkInFormRef.current?.validate();
      }

      newPass = applyFormResponseToPass(siteRequirements!.form!, checkInFormRef.current!.values, newPass);

      await createPass(newPass);
    } catch (error) {
      addNotification({
        heading: 'Your form response has been rejected',
        message: 'Please review your responses',
        variant: 'danger',
      });
    }
  };

  const retrievePrefilledForm = async (newPass: PrePass) => {
    // retrieve the prefilled form and set it on a state
    // the check-in form is going to use this value to show the latest form responses

    const formPrefill = await formService.retrieveFormPrefill(newPass.siteId, newPass, 'signin');

    const _prefilledForm = formService.applyPrefilledFieldByVisitorType(
      formPrefill,
      siteRequirements!.form!,
      visitorDetailsFormRef.current!.values.visitorType!.value as string,
    );

    setPrefilledForm(_prefilledForm);
  };

  const createPrePass = async () => {
    // get the identity token
    // if form is prefilled retrieve the form using the prePass
    // if there is a check-in form set the prePass on a state and go to the check-in form
    // if there's no check-in form, create a new pass using the prePass

    let shouldCloseModal = false;

    try {
      addNotification({
        message: 'Processing your check in',
        variant: 'info',
      });

      const identityToken = await identityCheck();

      const {
        firstName,
        lastName,
        email,
        mobile,
        company,
        companyId,
        companySource,
        host: selectedHost,
        zone: selectedZone,
      } = visitorDetailsFormRef.current!.validateAndTransform().values;

      const { photoURL } = prefillPass;

      const siteId = selectedSite!.value as string;
      const site = { id: selectedSite!.value as string };
      const visitorType = selectedVisitorType!.value as string;

      // eslint-disable-next-line prefer-const
      let newPass: PrePass = {
        firstName,
        lastName,
        email,
        mobile,
        visitorType,
        company,
        companyId,
        companySource,
        siteId,
        site,
        identityToken,
        photoURL,
      };

      if (selectedHost) {
        newPass['host'] = { id: selectedHost.value as string };
        newPass['hostId'] = selectedHost.value as string;
      }

      if (selectedZone) {
        newPass['zoneId'] = selectedZone.value as string;
      }

      const isPrefilled = !!(invitation || pass);

      if (hasCheckInForm) {
        // If there are pre-registered invitation form responses, apply them first instead of retrieving the prefilled form
        if (
          invitationWithFormResponses &&
          'formResponses' in invitationWithFormResponses &&
          invitationWithFormResponses.formResponses
        ) {
          const _prefilledForm = formService.applyInvitationFormResponseByVisitorType(
            invitationWithFormResponses.formResponses as FormResult,
            siteRequirements.form!,
            visitorType,
          );

          if ('responses' in _prefilledForm && _prefilledForm.responses.length > 0) {
            setPrefilledForm(_prefilledForm);
          } else if (isPrefilled) {
            await retrievePrefilledForm(newPass);
          }
        } else if (isPrefilled) {
          await retrievePrefilledForm(newPass);
        }

        if (formRequired) {
          setNewPassForm(newPass);
          setPage(PAGE.CHECK_IN_FORM);
        }
      }

      if (!hasCheckInForm || !formRequired) {
        await createPass(newPass);
      }
    } catch (error) {
      let errorHeading = '';

      if (error.code === 'CHECK_IN_NOT_PERMITTED') {
        errorHeading = `Sorry, we couldn't sign this person in!`;
        error.customMsg = 'This pass has been rejected';
        shouldCloseModal = true;
      } else if (error.code === 'PASS_ALREADY_EXISTS' && error.info === 'PASS_PENDING_WITH_HOST') {
        error.customMsg = 'A pass is awaiting host approval';
      } else if (error.code === 'PASS_ALREADY_EXISTS') {
        const { firstName, lastName } = visitorDetailsFormRef.current!.values;
        const visitorFullName = firstName + ' ' + lastName;
        error.customMsg = visitorFullName + ' already checked in.';
      }

      addNotification({
        heading: errorHeading,
        message: parseServerError(error),
        variant: 'danger',
      });
    } finally {
      shouldCloseModal && onClose();
    }
  };

  const validateVisitorForm = async () => {
    // save check-in-another setting
    // create prePass if visitor details form is valid
    storage.setItem('checkinAnother', String(checkInAnother));

    if (visitorDetailsFormRef.current?.validate()) {
      await createPrePass();
    }
  };

  const onNext = async () => {
    // run visitor details form validation

    await validateVisitorForm();
  };

  const onCheckIn = async () => {
    // if onnext was not run, run them

    if (!hasCheckInForm || !formRequired) {
      await validateVisitorForm();
    } else {
      await applyFormResponses(newPassForm!);
    }
  };

  const onBack = () => {
    setPage(PAGE.VISITOR_DETAILS), setEditVisitorDetailsForm(true);
  };

  return (
    <>
      {!isFetchingSites && (
        <StyledModal
          show={show}
          size='md'
          onClose={onClose}
          header={(page === PAGE.CHECK_IN_FORM && siteRequirements?.form?.name) || 'Visitor Check-in'}
          footer={
            <FooterContainer rightAlign={!!pass || !!invitation}>
              {!invitation && !pass && (
                <StyledCheckBox
                  label='Check-in another guest'
                  onChange={setCheckInAnother}
                  value={checkInAnother}
                  data-st-ext='checkbox-another-guest'
                />
              )}

              <ButtonGroup alignment='right'>
                {page === PAGE.VISITOR_DETAILS && (
                  <Button variant='tertiary' onClick={onClose} data-st-ext='button-cancel'>
                    Cancel
                  </Button>
                )}

                {page === PAGE.CHECK_IN_FORM && (
                  <Button variant='tertiary' onClick={onBack} data-st-ext='button-back'>
                    Back
                  </Button>
                )}

                {page === PAGE.VISITOR_DETAILS && hasCheckInForm && formRequired && (
                  <Button variant='primary' onClick={onNext} data-st-ext='button-next'>
                    Next
                  </Button>
                )}

                {((page === PAGE.VISITOR_DETAILS && !hasCheckInForm) ||
                  !formRequired ||
                  page === PAGE.CHECK_IN_FORM) && (
                  <Button variant='primary' onClick={onCheckIn} data-st-ext='button-check-in'>
                    Check-in
                  </Button>
                )}
              </ButtonGroup>
            </FooterContainer>
          }
        >
          <Loader overlay show={isLoading} />

          {page === PAGE.VISITOR_DETAILS ? (
            <VisitorDetailsForm
              formRef={visitorDetailsFormRef}
              isLoading={isLoading}
              siteRequirements={siteRequirements as SigninRequirements}
              pass={prefillPass}
              siteOptions={siteOptions}
              onSiteChange={setSelectedSite}
              onVisitorTypeChange={setSelectedVisitorType}
              editVisitorDetailsForm={editVisitorDetailsForm}
              checkInAnother={checkInAnother}
              firstNameInput={firstNameInput}
            />
          ) : (
            page === PAGE.CHECK_IN_FORM && (
              <SiteCheckInForm
                formRef={checkInFormRef}
                siteRequirements={siteRequirements as SigninRequirements}
                visitorTypeId={selectedVisitorType!.value as string}
                prefilledForm={prefilledForm}
              />
            )
          )}
        </StyledModal>
      )}
    </>
  );
}

VisitorCheckInModal.propTypes = {
  invitation: PropTypes.object,
  pass: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
};
